/**
 * ItemDetailPage — public product detail page for a marketplace item.
 *
 * Shopify-style:
 *   • Image gallery (left on desktop, stacked on mobile)
 *   • Title, price, variant picker, qty stepper, Add-to-Cart + Buy-Now
 *   • Vendor card linking to /vendor/:vendorId
 *   • "More from this vendor" row
 *   • "Shop more items" link back to /shop
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader2, Store, Package, Truck, ChevronLeft, ShoppingBag, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSeo from '@/hooks/useSeo';
import { useMarketplaceCart } from '@/context/MarketplaceCartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function abs(url) {
  if (!url) return '';
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

export default function ItemDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { addToCart, wouldReplaceCart } = useMarketplaceCart();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [qty, setQty] = useState(1);

  useSeo({
    title: item ? `${item.title} · EntrepreNatives` : 'Shop · EntrepreNatives',
    description: item?.description?.slice(0, 160) || 'Shop handcrafted Indigenous-made items on EntrepreNatives.',
  });

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/public/items/${itemId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        setItem(data);
        // Default-select the first in-stock variant if the item has variants.
        if (data.variants && data.variants.length > 0) {
          const firstInStock = data.variants.find((v) => (v.inventory || 0) > 0) || data.variants[0];
          setSelectedOptions(firstInStock.option_values || {});
        }
      })
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [itemId]);

  // Resolve the active variant from the selected option values.
  const activeVariant = useMemo(() => {
    if (!item?.variants?.length) return null;
    return item.variants.find((v) =>
      (item.option_names || []).every(
        (name) => (v.option_values || {})[name] === selectedOptions[name],
      ),
    ) || null;
  }, [item, selectedOptions]);

  const price = activeVariant?.price != null ? activeVariant.price : item?.base_price || 0;
  const inStock = activeVariant
    ? (activeVariant.inventory || 0) > 0 || !item?.track_inventory
    : item?.in_stock ?? true;
  const maxQty = activeVariant
    ? (item?.track_inventory ? (activeVariant.inventory || 0) : 99)
    : (item?.track_inventory ? (item?.inventory || 0) : 99);

  const chooseOption = (name, value) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddToCart = (buyNow = false) => {
    if (!item) return;
    // Guard: must have picked a variant if the item has option_names.
    if ((item.option_names || []).length > 0 && !activeVariant) {
      toast.error('Please pick a variant first');
      return;
    }
    if (!inStock) {
      toast.error('This item is sold out');
      return;
    }
    const replacePrompt = wouldReplaceCart(item.vendor_id);
    if (replacePrompt) {
      if (!window.confirm('Your cart already contains items from another vendor. Clear it and start a new cart?')) {
        return;
      }
      addToCart(item, activeVariant, qty, { replace: true });
    } else {
      addToCart(item, activeVariant, qty);
    }
    if (buyNow) {
      navigate('/shop/checkout');
    } else {
      toast.success(`Added ${qty} × ${item.title}${activeVariant ? ' (' + Object.values(activeVariant.option_values || {}).join(' / ') + ')' : ''} to cart`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A23] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#8B5CF6]" />
      </div>
    );
  }
  if (!item) {
    return (
      <div className="min-h-screen bg-[#0A0A23] text-white">
        <Navbar />
        <div className="container-main pt-32 pb-20 text-center">
          <h1 className="font-syne text-3xl font-bold">Item not found</h1>
          <p className="mt-3 text-[#9CA3AF]">It may have been hidden or sold out.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 mt-6 text-[#C4B5FD] hover:text-white">
            <ChevronLeft size={16} /> Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  const images = item.images && item.images.length > 0 ? item.images : [''];
  const compareAt = item.compare_at_price;

  return (
    <div className="min-h-screen bg-[#0A0A23] text-white">
      <Navbar />

      <main className="pt-24 pb-24">
        <div className="container-main">
          <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-[#9CA3AF] hover:text-[#C4B5FD] mb-6">
            <ChevronLeft size={14} /> Back to Shop
          </Link>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            {/* ─── Gallery ─────────────────────────────────────────── */}
            <div>
              <div className="relative aspect-square rounded-3xl overflow-hidden bg-[#1A1D4A] border border-white/5 shadow-[0_30px_60px_-20px_rgba(139,92,246,0.5)]">
                {images[activeImage] ? (
                  <img src={abs(images[activeImage])} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]">
                    <Package size={64} strokeWidth={1} />
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="mt-4 grid grid-cols-5 gap-2.5">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${i === activeImage ? 'border-[#8B5CF6] shadow-[0_0_18px_rgba(139,92,246,0.6)]' : 'border-white/10 hover:border-white/30'}`}
                      data-testid={`item-image-thumb-${i}`}
                    >
                      <img src={abs(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ─── Details ─────────────────────────────────────────── */}
            <div>
              {/* Vendor pill */}
              {item.vendor && (
                <Link
                  to={`/vendor/${item.vendor.vendor_id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-[#C4B5FD] hover:bg-white/10 transition-all"
                  data-testid="item-vendor-pill"
                >
                  <Store size={12} strokeWidth={1.5} />
                  Sold by <span className="font-semibold text-white">{item.vendor.business_name}</span>
                </Link>
              )}

              <h1 className="mt-4 font-syne text-3xl md:text-4xl font-bold tracking-tight leading-tight" data-testid="item-title">
                {item.title}
              </h1>

              <div className="mt-3 flex items-baseline gap-3">
                <span className="text-3xl font-bold text-white" data-testid="item-price">${Number(price).toFixed(2)}</span>
                {compareAt && (
                  <span className="text-sm text-[#9CA3AF] line-through">${Number(compareAt).toFixed(2)}</span>
                )}
                {!inStock && (
                  <span className="ml-2 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-red-500/20 text-[#FCA5A5] border border-red-500/30">Sold out</span>
                )}
              </div>

              {/* Option pickers (one row per option name) */}
              {(item.option_names || []).length > 0 && (
                <div className="mt-7 space-y-5" data-testid="item-options">
                  {item.option_names.map((name) => {
                    const values = Array.from(new Set((item.variants || []).map((v) => v.option_values?.[name]).filter(Boolean)));
                    return (
                      <div key={name}>
                        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold mb-2">{name}</p>
                        <div className="flex flex-wrap gap-2">
                          {values.map((val) => {
                            const selected = selectedOptions[name] === val;
                            // Compute if this value is in stock (any variant with this value available).
                            const anyInStock = (item.variants || []).some(
                              (v) => v.option_values?.[name] === val && ((v.inventory || 0) > 0 || !item.track_inventory),
                            );
                            return (
                              <button
                                key={val}
                                onClick={() => chooseOption(name, val)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${selected ? 'bg-white text-[#0E0E23] border-white' : 'bg-white/5 text-white border-white/10 hover:border-white/40'} ${!anyInStock ? 'opacity-40 line-through' : ''}`}
                                data-testid={`item-option-${name}-${val}`}
                              >
                                {val}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Qty stepper */}
              <div className="mt-7" data-testid="item-qty">
                <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold mb-2">Quantity</p>
                <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full">
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-10 h-10 flex items-center justify-center text-[#C4B5FD] hover:text-white"
                    data-testid="item-qty-dec"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-10 text-center font-semibold" data-testid="item-qty-value">{qty}</span>
                  <button
                    onClick={() => setQty((q) => Math.min(maxQty || 99, q + 1))}
                    className="w-10 h-10 flex items-center justify-center text-[#C4B5FD] hover:text-white"
                    data-testid="item-qty-inc"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                {item.track_inventory && maxQty > 0 && maxQty < 10 && (
                  <p className="mt-1.5 text-[11px] text-[#FCD34D]">Only {maxQty} left in stock</p>
                )}
              </div>

              {/* CTAs */}
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => handleAddToCart(false)}
                  disabled={!inStock}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm bg-white/5 border border-white/10 text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="item-add-to-cart"
                >
                  <ShoppingBag size={16} /> Add to cart
                </button>
                <button
                  onClick={() => handleAddToCart(true)}
                  disabled={!inStock}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_45px_rgba(139,92,246,0.7)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="item-buy-now"
                >
                  Buy it now
                </button>
              </div>

              {/* Shipping hint */}
              <div className="mt-6 flex items-center gap-2 text-xs text-[#9CA3AF]">
                <Truck size={13} strokeWidth={1.5} />
                {item.free_shipping ? 'Free shipping on this item' : 'Shipping calculated at checkout'}
              </div>

              {/* Description */}
              {item.description && (
                <div className="mt-10 pt-8 border-t border-white/10">
                  <h2 className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold mb-3">About this item</h2>
                  <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed" data-testid="item-description">{item.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* ─── More from this vendor ──────────────────────────────── */}
          {item.more_from_vendor && item.more_from_vendor.length > 0 && (
            <section className="mt-20" data-testid="item-more-from-vendor">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-syne text-xl md:text-2xl font-bold">
                  More from {item.vendor?.business_name || 'this vendor'}
                </h2>
                {item.vendor && (
                  <Link to={`/vendor/${item.vendor.vendor_id}`} className="text-sm text-[#C4B5FD] hover:text-white">
                    View shop →
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {item.more_from_vendor.map((other) => (
                  <Link
                    key={other.item_id}
                    to={`/shop/item/${other.item_id}`}
                    className="group"
                  >
                    <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#1A1D4A] border border-white/5 shadow-[0_10px_30px_-10px_rgba(139,92,246,0.5)]">
                      {other.images?.[0] ? (
                        <img src={abs(other.images[0])} alt={other.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]"><Package size={32} /></div>
                      )}
                    </div>
                    <p className="mt-3 text-sm font-semibold text-white group-hover:text-[#C4B5FD] line-clamp-2">{other.title}</p>
                    <p className="text-xs text-[#9CA3AF]">${Number(other.base_price).toFixed(2)}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
